import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  input,
  Input,
  LOCALE_ID,
  Output,
  signal,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";

import { ColDef } from "ag-grid-community";

import { ILineITemExtended } from "@components/purchase-orders/models/purchase-order.interface";
import { ConfirmDialogComponent } from "@components/shared";
import { LinkCellRendererComponent, QuickActionsMenuComponent } from "@shared/cell-renderers";
import { IconConstants, TextConstants } from "@shared/constants";
import { ConfirmDialogResponseEnum, TableEnum } from "@shared/enums";
import { AuthenticationService } from "@shared/services";
import { RouterService } from "@shared/services/router.service";
import { ColumnUtils, CommonUtils } from "@shared/utils";

import { AddPurchaseOrderItemDialogComponent } from "../add-purchase-order-item-dialog/add-purchase-order-item-dialog.component";

@Component({
  selector: "app-line-items-table",
  templateUrl: "./line-items-table.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LineItemsTableComponent implements AfterViewInit {
  public columnDefs = signal<ColDef[]>([]);

  public isLoading = signal(true);

  private readonly routerService = inject(RouterService);

  private readonly authenticationService = inject(AuthenticationService);

  private readonly dialog = inject(MatDialog);

  private readonly locale: string = inject(LOCALE_ID);

  @Input()
  set rowData(value: ILineITemExtended[]) {
    this._rowData =
      value?.map((item) => {
        if (item.product?.defaultCustomUnit) {
          item.quantityFormatted = CommonUtils.formatQuantityWithDefaultUnit(
            this.locale,
            item.quantity,
            item.product.defaultCustomUnit,
            item.product.unitOfMeasurement,
          );
        } else {
          item.quantityFormatted = item.quantity.toString();
        }

        return item;
      }) ?? [];
  }

  get rowData(): ILineITemExtended[] {
    return this._rowData;
  }

  private _rowData: ILineITemExtended[] = [];

  private readonly hasEntitiesWriteAccess = this.authenticationService.hasEntitiesWriteAccess;

  public areButtonsEnabled = input<boolean>(true);

  public readonly table = TableEnum.PURCHASE_ORDERS;

  @Output()
  removeLineItem: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  lineItemUpdated: EventEmitter<string> = new EventEmitter<string>();

  @Input()
  public columns: string[] = [
    "lineItemId",
    "product.name",
    "quantityFormatted",
    "product.unitOfMeasurement.symbol",
  ];

  private setColumnDefs = (): void => {
    let columnDefs: ColDef[] = [
      {
        headerName: TextConstants.ITEM_ID_NUMBER,
        field: "lineItemId",
        lockVisible: true,
        ...ColumnUtils.quickActionsMenuColumnCommonValues,
        cellRenderer: QuickActionsMenuComponent,
        cellRendererParams: {
          isLinkStyle: this.hasEntitiesWriteAccess,
          actions:
            this.areButtonsEnabled() && this.hasEntitiesWriteAccess
              ? [
                  {
                    icon: IconConstants.DELETE,
                    tooltip: TextConstants.REMOVE,
                    click: this.onDelete,
                  },
                  {
                    icon: IconConstants.EDIT,
                    tooltip: TextConstants.EDIT,
                    click: this.onEdit,
                  },
                ]
              : [],
        },
      },
      {
        headerName: TextConstants.PRODUCT,
        field: "product.name",
        lockVisible: true,
        cellRenderer: LinkCellRendererComponent,
        cellRendererParams: {
          linkRouteIdParam: "product.id",
          linkRouteFn: this.routerService.getProductLink,
        },
      },
      {
        headerName: TextConstants.QUANTITY,
        field: "quantityFormatted",
        lockVisible: true,
      },
      {
        headerName: $localize`:@@lineitemTable.unit:Unit`,
        field: "product.unitOfMeasurement.symbol",
        lockVisible: true,
      },
    ];

    columnDefs = CommonUtils.getVisibleColumnDefs(columnDefs, this.columns);
    this.columnDefs.set(columnDefs);
  };

  ngAfterViewInit() {
    this.isLoading.set(true);
    this.setColumnDefs();
    this.isLoading.set(false);
  }

  onDelete = (row: ILineITemExtended) => {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: TextConstants.DELETE_CONFIRMATION,
        contentText: $localize`:@@confirm.lineitemTable.deleteLineItem:Are you sure you want to remove this item from the purchase order?`,
        confirmButtonColor: "danger",
        confirmButtonText: TextConstants.REMOVE,
        confirmButtonIcon: IconConstants.DELETE,
      },
    });

    dialogRef.afterClosed().subscribe(async (result: ConfirmDialogResponseEnum) => {
      if (result === ConfirmDialogResponseEnum.CONFIRM) {
        this.removeLineItem.emit(row.lineItemId);
      }
    });
  };

  onEdit = (row: ILineITemExtended) => {
    const dialogRef = this.dialog.open(AddPurchaseOrderItemDialogComponent, {
      data: {
        lineItem: row,
        allLineItems: this.rowData,
      },
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result?.hasSaved) {
        this.lineItemUpdated.emit();
      }
    });
  };

  onClickRow(row: ILineITemExtended) {
    if (this.hasEntitiesWriteAccess) {
      this.onEdit(row);
    }
  }
}
